/**
 * Header Component
 * Top navigation bar with logo, breadcrumbs, and user actions
 */

import React from 'react';
import {
  AppBar,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PropTypes from 'prop-types';

import ProfileSection from './ProfileSection';
import NotificationSection from './NotificationSection';
import { buildBreadcrumbs } from '../config/navigation';
import { ENV, resolveBrowserLogoPath } from '../config/environment';

/**
 * Header
 * Application header with navigation and user controls
 * 
 * @param {Object} props
 * @param {Function} props.onToggleSidebar - Callback to toggle sidebar
 * @returns {React.ReactNode}
 */
const Header = ({ onToggleSidebar }) => {
  const theme = useTheme();
  const location = useLocation();
  const browserClientName = ENV.BROWSER_CLIENT_NAME || 'MDM';
  const logoSrc = resolveBrowserLogoPath();

  const breadcrumbs = buildBreadcrumbs(location.pathname);

  return (
    <AppBar
    className='p-0'
      position="fixed"
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: 'none',
        border: 'none',
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{
        py:1.6
      }}>

        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 3,
          }}
        >
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt={`${browserClientName} logo`}
              sx={{
                height: 45,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography
              variant="h3"
              component={RouterLink}
              to="/home"
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 700,
                '&:hover': {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              {browserClientName}
            </Typography>
          )}
        </Box>

        {/* Menu Icon */}
        {/* <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle sidebar"
          onClick={onToggleSidebar}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton> */}

        {/* Breadcrumbs */}
        <Box sx={{ flexGrow: 1 }}>
          {/* <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: theme.palette.text.secondary,
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              
              if (isLast || !crumb.path) {
                return (
                  <Typography
                    key={crumb.label}
                    variant="body2"
                    sx={{
                      color: isLast ? theme.palette.text.primary : theme.palette.text.secondary,
                      fontWeight: isLast ? 600 : 400,
                    }}
                  >
                    {crumb.label}
                  </Typography>
                );
              }

              return (
                <Link
                  key={crumb.label}
                  component={RouterLink}
                  to={crumb.path}
                  underline="hover"
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Typography variant="body2">{crumb.label}</Typography>
                </Link>
              );
            })}
          </Breadcrumbs> */}
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          {/* <NotificationSection /> */}

          {/* User Profile */}
          <ProfileSection />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

Header.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
};

export default Header;
